import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box } from '@mui/material';
import { createOrganization } from '../api.ts';
import type { Organization } from '../models/organization.ts';
import { useAppDispatch } from '../store/hooks.ts';
import { addOrganization } from '../store/organization/organizationSlice.ts';
import type { OrganizationWithRole } from '../api.ts';
import { formatApiError } from '../utils/errorHandling.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (org: Organization) => void;
};

const CreateOrganizationDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      setError('Organization name is required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const org = await createOrganization({ name, slug: '' } as Organization);

      // Convert Organization to OrganizationWithRole (user becomes owner of new org)
      const created: OrganizationWithRole = {
        ...org,
        role: 'owner',
        is_default: false,
      };

      // Update local store
      dispatch(addOrganization(created));

      // Notify parent
      onCreated && onCreated(org);

      // Reset
      setName('');
      onClose();
    } catch (err: any) {
      setError(formatApiError(err, 'Failed to create organization'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box component="form" onSubmit={handleCreate}>
        <DialogTitle>Create New Organization</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            disabled={isLoading}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => {
              onClose();
              setName('');
              setError(null);
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || !name.trim()}>
            Create
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateOrganizationDialog;
